import React from 'react';
import { Route, Ruler, DollarSign, Droplet } from 'lucide-react';

/**
 * Компонент основных параметров для расчета расхода топлива
 */
const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  fuelTypes,
  consumptionUnits,
  distanceUnits,
  priceUnits
}) => {
  // Определяем, какие поля показывать в зависимости от типа расчета
  const showDistanceField = formData.calculationType === 'consumption' || formData.calculationType === 'cost';
  const showConsumptionField = formData.calculationType === 'distance' || formData.calculationType === 'cost';
  const showPriceField = formData.calculationType === 'consumption' || formData.calculationType === 'distance';

  return (
    <div className="mb-6">
      {/* Выбор типа расчета */}
      <div className="mb-5">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Что вы хотите рассчитать?
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors
            ${formData.calculationType === 'consumption' 
              ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700' 
              : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}`}>
            <input
              type="radio"
              name="calculationType"
              value="consumption"
              checked={formData.calculationType === 'consumption'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 block">
              <span className="block text-sm font-medium">Расход топлива</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">На основе расстояния и затрат</span>
            </span>
          </label>
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors
            ${formData.calculationType === 'distance' 
              ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700' 
              : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}`}>
            <input
              type="radio"
              name="calculationType"
              value="distance"
              checked={formData.calculationType === 'distance'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 block">
              <span className="block text-sm font-medium">Расстояние</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">На основе расхода и затрат</span>
            </span>
          </label>
          <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors
            ${formData.calculationType === 'cost' 
              ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:border-blue-700' 
              : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'}`}>
            <input
              type="radio"
              name="calculationType"
              value="cost"
              checked={formData.calculationType === 'cost'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 block">
              <span className="block text-sm font-medium">Стоимость поездки</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">На основе расстояния и расхода</span>
            </span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Расстояние */}
        {showDistanceField && (
          <div>
            <label htmlFor="distance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Route className="w-4 h-4 mr-2 text-blue-500" />
              Расстояние
            </label>
            <div className="flex">
              <input
                type="text"
                id="distance"
                name="distance"
                value={formData.distance}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.distance ? 'input-error' : ''}`}
                placeholder="Введите расстояние"
              />
              <select
                name="distanceUnit"
                value={formData.distanceUnit}
                onChange={handleInputChange}
                className="form-select ml-2 w-1/3"
              >
                {distanceUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </div>
            {errors.distance && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.distance}
              </p>
            )}
          </div>
        )}

        {/* Расход топлива */}
        {showConsumptionField && (
          <div>
            <label htmlFor="fuelConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Ruler className="w-4 h-4 mr-2 text-blue-500" />
              Расход топлива
            </label>
            <div className="flex">
              <input
                type="text"
                id="fuelConsumption"
                name="fuelConsumption"
                value={formData.fuelConsumption}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.fuelConsumption ? 'input-error' : ''}`}
                placeholder="Введите расход"
              />
              <select
                name="consumptionUnit"
                value={formData.consumptionUnit}
                onChange={handleInputChange}
                className="form-select ml-2 w-1/3"
              >
                {consumptionUnits
                  .filter(unit => formData.fuelType === 'electricity' ? unit.forElectric !== false : !unit.forElectric)
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))
                }
              </select>
            </div>
            {errors.fuelConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.fuelConsumption}
              </p>
            )}
          </div>
        )}

        {/* Цена топлива */}
        {showPriceField && (
          <div>
            <label htmlFor="fuelPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
              Цена топлива
            </label>
            <div className="flex">
              <input
                type="text"
                id="fuelPrice"
                name="fuelPrice"
                value={formData.fuelPrice}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.fuelPrice ? 'input-error' : ''}`}
                placeholder="Введите цену"
              />
              <select
                name="priceUnit"
                value={formData.priceUnit}
                onChange={handleInputChange}
                className="form-select ml-2 w-1/3"
              >
                {priceUnits
                  .filter(unit => formData.fuelType === 'electricity' ? unit.forElectric !== false : !unit.forElectric)
                  .map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))
                }
              </select>
            </div>
            {errors.fuelPrice && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.fuelPrice}
              </p>
            )}
          </div>
        )}

        {/* Тип топлива */}
        <div>
          <label htmlFor="fuelType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Droplet className="w-4 h-4 mr-2 text-blue-500" />
            Тип топлива
          </label>
          <select
            id="fuelType"
            name="fuelType"
            value={formData.fuelType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.fuelType ? 'input-error' : ''}`}
          >
            {fuelTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({type.defaultPrice.toFixed(2)} ₽/{type.unit || 'л'})
              </option>
            ))}
          </select>
          {errors.fuelType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.fuelType}
            </p>
          )}
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
        <p className="flex items-start">
          <Droplet className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-blue-500 dark:text-blue-400" />
          <span>
            В России средний расход топлива городских автомобилей составляет 8-12 л/100 км для бензина и 6-9 л/100 км для дизеля. 
            На трассе расход снижается на 20-30%. Цены на топливо в 2025 году: АИ-92 — от 51 до 53 ₽/л, АИ-95 — от 54 до 57 ₽/л, 
            дизель — от 57 до 60 ₽/л в зависимости от региона.
          </span>
        </p>
      </div>
    </div>
  );
};

export default BasicParametersSection;